"use client";

import React from "react";
import { FlaskConical, Layers, RefreshCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

declare global {
  interface Window {
    wvcClient?: {
      getCurrentWPTermQueryParams: () => Record<string, any>;
    };
  }
}

function DynamicShopBreadcrumbs() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-1">
        <div className="h-4 w-16 animate-pulse rounded bg-muted" />
        <span className="text-muted-foreground">/</span>
        <div className="h-4 w-24 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (error || !links || links.length === 0) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/HomePage">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/ShopPage">Shop</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Archive</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {links.map((link, index) => (
          <React.Fragment key={link.href || index}>
            <BreadcrumbItem>
              {index === lastIndex ? (
                <BreadcrumbPage className="font-medium text-foreground">
                  {link.title}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={link.href}>{link.title}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < lastIndex && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitleHeading() {
  const { terms, loading, error, refetch } = useEcommerceTerms();

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-10 w-64 animate-pulse rounded-md bg-muted md:w-96" />
        <div className="h-5 w-full max-w-xl animate-pulse rounded-md bg-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 text-sm text-destructive">
        <span>Failed to load archive details.</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="h-8 gap-1.5 px-3 text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  const currentTerm = terms && terms.length > 0 ? terms[0] : null;
  const title = currentTerm?.name || "Research peptides and compounds";
  const description =
    currentTerm?.description ||
    "Find laboratory-grade reagents with verified purity documentation and batch specifications.";
  const count = currentTerm?.count;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-default text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          {title}
        </h1>
        {typeof count === "number" && (
          <span className="inline-flex items-center rounded-full bg-accent px-3 py-1 font-mono text-xs font-medium text-accent-foreground">
            {count} {count === 1 ? "item" : "items"}
          </span>
        )}
      </div>
      <p className="max-w-3xl text-base text-muted-foreground sm:text-lg">
        {description}
      </p>
    </div>
  );
}

export default function ArchiveTitle() {
  const currentParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPTermQueryParams()
      : { taxonomy: "product_cat" };

  const wpTermQuery = new WP_Term_Query(currentParams);

  return (
    <section
      id="archive-title"
      data-nav="light"
      className="border-b border-border bg-background py-8 sm:py-12"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border/60 pb-4">
            <ShopBreadcrumbsProvider>
              <DynamicShopBreadcrumbs />
            </ShopBreadcrumbsProvider>

            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
                <Link to="/ShopPage">
                  <Layers className="h-3.5 w-3.5" />
                  All products
                </Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                <Link to="/ResearchInformationPage">
                  <FlaskConical className="h-3.5 w-3.5" />
                  Quality control
                </Link>
              </Button>
            </div>
          </div>

          <EcommerceTermsProvider wp_term_query={wpTermQuery}>
            <ArchiveTitleHeading />
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>
  );
}